import { useState, useEffect } from "react";
import {
	Modal,
	Form,
	Input,
	Select,
	Rate,
	message,
	Spin,
	DatePicker,
} from "antd";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import useSWR from "swr";

import appClientStore from "../../lib/store/appClient";
import ReviewService, {
	CreateReviewState,
} from "../../network/services/reviews";
import ProductService, {
	Product,
	ProductVariant,
} from "../../network/services/product";

interface CreateReviewModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
}

const CreateReviewModal: React.FC<CreateReviewModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
}) => {
	const { t } = useTranslation();
	const {
		state: { id: appClientId },
	} = useSnapshot(appClientStore);
	const [form] = Form.useForm<CreateReviewState>();
	const [isLoading, setIsLoading] = useState(false);

	// Fetch products for dropdown
	const { data: productsResponse, isLoading: isLoadingProducts } = useSWR(
		appClientId ? ProductService.getAll(appClientId) : null,
		{ refreshInterval: 0 },
	);

	const products = ProductService.toRow(productsResponse);
	const selectedExternalProductId = Form.useWatch("product_id", form);
	const selectedProductId =
		products.find((p: Product) => p.custom_id === selectedExternalProductId)
			?.id ?? "";
	const { data: selectedProductResponse, isLoading: isLoadingProduct } = useSWR(
		appClientId && selectedProductId
			? ProductService.get(appClientId, selectedProductId)
			: null,
		{ refreshInterval: 0 },
	);
	const hasVariants = selectedProductResponse?.variants?.length > 0;
	useEffect(() => {
		if (!isOpen) {
			form.resetFields();
		}
	}, [isOpen, form]);

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();

			if (!appClientId) {
				message.error(t("reviewCreateError"));
				return;
			}

			setIsLoading(true);

			await ReviewService.create(appClientId, {
				product_id: values.product_id,
				variant_id: values.variant_id,
				score: values.score,
				email: values.email,
				name: values.name,
				title: values.title,
				content: values.content,
				created_at: values.created_at
					? values.created_at.toISOString()
					: undefined,
			});

			message.success(t("reviewCreatedSuccess"));
			form.resetFields();
			onSuccess();
			onClose();
		} catch (error: any) {
			if (error.errorFields) {
				// Form validation error
				return;
			}
			message.error(error.message || t("reviewCreateError"));
		} finally {
			setIsLoading(false);
		}
	};

	const handleCancel = () => {
		if (!isLoading) {
			form.resetFields();
			onClose();
		}
	};

	return (
		<Modal
			open={isOpen}
			title={t("createNewReview")}
			onCancel={handleCancel}
			onOk={handleSubmit}
			okText={t("create")}
			cancelText={t("cancel")}
			confirmLoading={isLoading}
			cancelButtonProps={{
				disabled: isLoading,
			}}
			width={600}
		>
			{isLoadingProducts ? (
				<div style={{ textAlign: "center", padding: "40px 0" }}>
					<Spin />
				</div>
			) : (
				<Form form={form} layout="vertical" requiredMark="optional">
					<Form.Item
						name="product_id"
						label={t("product")}
						rules={[
							{
								required: true,
								message: t("selectProduct"),
							},
						]}
					>
						<Select
							placeholder={t("selectProduct")}
							showSearch
							optionFilterProp="children"
							filterOption={(input, option) =>
								(option?.label ?? "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							onChange={() => form.setFieldValue("variant_id", undefined)}
							options={products.map((p: Product) => ({
								value: p.custom_id,
								label: p.name,
							}))}
						/>
					</Form.Item>

					{hasVariants && (
						<Form.Item
							name="variant_id"
							label={t("variant", "Variant")}
							rules={[
								{
									required: true,
									message: t("selectVariant", "Please select a variant"),
								},
							]}
						>
							<Select
								placeholder={t("selectVariant", "Select a variant")}
								showSearch
								loading={isLoadingProduct}
								optionFilterProp="children"
								filterOption={(input, option) =>
									String(option?.label ?? "")
										.toLowerCase()
										.includes(input.toLowerCase())
								}
								options={(selectedProductResponse?.variants || []).map(
									(v: ProductVariant) => ({
										value: v.custom_id || v.id,
										label: v.name,
									}),
								)}
							/>
						</Form.Item>
					)}

					<Form.Item
						name="score"
						label={t("score")}
						rules={[
							{
								required: true,
								message: "Please select a score",
							},
						]}
					>
						<Rate />
					</Form.Item>

					<Form.Item
						name="email"
						label={t("reviewerEmail")}
						rules={[
							{
								required: true,
								type: "email",
								message: "Please enter a valid email",
							},
						]}
					>
						<Input placeholder={t("reviewerEmail")} />
					</Form.Item>

					<Form.Item
						name="name"
						label={t("reviewerName")}
						rules={[
							{
								required: true,
								message: "Please enter reviewer name",
							},
						]}
					>
						<Input placeholder={t("reviewerName")} />
					</Form.Item>

					<Form.Item name="title" label={t("reviewTitle")}>
						<Input placeholder={t("reviewTitle")} />
					</Form.Item>

					<Form.Item name="content" label={t("reviewContent")}>
						<Input.TextArea rows={4} placeholder={t("reviewContent")} />
					</Form.Item>

					<Form.Item
						name="created_at"
						label={t("reviewDateTime", "Review Date & Time")}
					>
						<DatePicker showTime style={{ width: "100%" }} />
					</Form.Item>
				</Form>
			)}
		</Modal>
	);
};

export default CreateReviewModal;
